// Brands Dropdown — renders a searchable brand grid (logo + name + part count),
// each card linking into the parts-finder filtered by brand. Data comes from
// GET /api/filters/brands. No-op unless the target container is on the page.
//
// Config (read from the TARGET element, not the script tag):
//   [data-parts-brands-dropdown]  — the container
//
// Behaviour preserved from the legacy embed: cards are built ONCE, then search
// only mutates CSS classes (matches float to front via `order`, mismatches dim
// and slide to back) — no DOM rebuild per keystroke.

import { getTopDocument } from '../shared/dom.js';
import { apiJson } from '../shared/api.js';

var _instanceId = 'pf-brands-' + Math.random().toString(36).substr(2, 9);

function findContainer() {
  return getTopDocument().querySelector('[data-parts-brands-dropdown]');
}

// ---------------------------------------------------------------------------
// Deterministic fallback colour + initials for brands without a logo
// ---------------------------------------------------------------------------
var _PALETTE = ['#d9272e', '#2c347c', '#0f766e', '#7c3aed', '#b45309', '#0369a1', '#be185d', '#15803d'];
function colorForName(name) {
  var hash = 0;
  for (var i = 0; i < (name || '').length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return _PALETTE[Math.abs(hash) % _PALETTE.length];
}
function initials(name) {
  var words = (name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}
function makeInitialsEl(name) {
  var doc = getTopDocument();
  var el = doc.createElement('div');
  el.className = 'pf-brand-initials';
  el.style.background = colorForName(name);
  el.textContent = initials(name);
  return el;
}

// ---------------------------------------------------------------------------
// Build a single brand card element. Reused by initial render and updates.
// ---------------------------------------------------------------------------
function buildBrandCard(brand) {
  var doc = getTopDocument();
  var name = brand.name || '';
  var logoUrl = brand.logoUrl || '';
  var href = '/parts-finder?sort=relevance&brand=' + encodeURIComponent(name);

  var card = doc.createElement('a');
  card.className = 'pf-brand-card';
  card.href = href;
  card.dataset.brandName = name.toLowerCase();

  var logoWrap = doc.createElement('div');
  logoWrap.className = 'pf-brand-logo-wrap';
  if (logoUrl) {
    var img = doc.createElement('img');
    img.className = 'pf-brand-logo';
    img.src = logoUrl;
    img.alt = name;
    img.onerror = function () {
      logoWrap.innerHTML = '';
      logoWrap.appendChild(makeInitialsEl(name));
    };
    logoWrap.appendChild(img);
  } else {
    logoWrap.appendChild(makeInitialsEl(name));
  }
  card.appendChild(logoWrap);

  var nameEl = doc.createElement('div');
  nameEl.className = 'pf-brand-name';
  nameEl.textContent = name;
  card.appendChild(nameEl);

  // Per-brand part count intentionally NOT rendered — brand cards show the
  // logo + name only (no "N parts" count).
  return card;
}

// ---------------------------------------------------------------------------
// Initial render — build all cards once; subsequent queries only mutate classes.
// ---------------------------------------------------------------------------
function renderBrands(grid, allBrands) {
  grid.innerHTML = '';
  allBrands.forEach(function (brand) {
    grid.appendChild(buildBrandCard(brand));
  });
}

// ---------------------------------------------------------------------------
// Apply search state to already-rendered cards (no DOM rebuild).
// Matches float first (order:0), dimmed slide to back (order:2).
// ---------------------------------------------------------------------------
function applyBrandQuery(grid, countBadge, q, totalCount) {
  var cards = grid.querySelectorAll('.pf-brand-card');
  var matchCount = 0;
  for (var i = 0; i < cards.length; i++) {
    var card = cards[i];
    var name = card.dataset.brandName || '';
    var isMatch = !q || name.indexOf(q) !== -1;
    if (isMatch) matchCount++;
    card.classList.toggle('pf-match', !!q && isMatch);
    card.classList.toggle('pf-dimmed', !!q && !isMatch);
  }
  if (q) {
    countBadge.textContent = matchCount + ' of ' + totalCount + ' match' + (matchCount !== 1 ? 'es' : '');
    countBadge.classList.add('has-query');
  } else {
    countBadge.textContent = totalCount + ' brand' + (totalCount !== 1 ? 's' : '');
    countBadge.classList.remove('has-query');
  }
}

// ---------------------------------------------------------------------------
// Build scaffold with skeleton loader grid
// ---------------------------------------------------------------------------
function buildScaffold(container) {
  var doc = getTopDocument();

  var wrap = doc.createElement('div');
  wrap.className = 'pf-brands-wrap';
  wrap.id = _instanceId;

  // Wrap all skeleton elements in one container so a single .remove() clears it
  var skelContainer = doc.createElement('div');

  // Skeleton search bar
  var skelBar = doc.createElement('div');
  skelBar.className = 'pf-brands-skel-bar';
  var skelInput = doc.createElement('div');
  skelInput.className = 'pf-brands-skel-input';
  var skelBadge = doc.createElement('div');
  skelBadge.className = 'pf-brands-skel-badge';
  skelBar.appendChild(skelInput);
  skelBar.appendChild(skelBadge);
  skelContainer.appendChild(skelBar);

  // Skeleton card grid
  var skelGrid = doc.createElement('div');
  skelGrid.className = 'pf-brands-skeleton-grid';
  for (var i = 0; i < 40; i++) {
    var skel = doc.createElement('div');
    skel.className = 'pf-brands-skel';
    skelGrid.appendChild(skel);
  }
  skelContainer.appendChild(skelGrid);
  wrap.appendChild(skelContainer);

  container.appendChild(wrap);
  return { wrap: wrap, skeleton: skelContainer };
}

// ---------------------------------------------------------------------------
// Wire up search — renders all cards once, then mutates classes on keyup.
// ---------------------------------------------------------------------------
function buildInteractiveUI(wrap, skeleton, allBrands) {
  var doc = getTopDocument();
  skeleton.remove();

  if (allBrands.length === 0) {
    var empty = doc.createElement('div');
    empty.className = 'pf-brands-empty';
    empty.textContent = 'No brands found.';
    wrap.appendChild(empty);
    return;
  }

  // Search row
  var searchRow = doc.createElement('div');
  searchRow.className = 'pf-brands-search-row';

  var searchInput = doc.createElement('input');
  searchInput.type = 'search';
  searchInput.className = 'pf-brands-search-input';
  searchInput.placeholder = 'Search brands…';
  searchInput.setAttribute('aria-label', 'Search brands');

  var countBadge = doc.createElement('span');
  countBadge.className = 'pf-brands-count-badge';
  countBadge.textContent = allBrands.length + ' brands';

  searchRow.appendChild(searchInput);
  searchRow.appendChild(countBadge);
  wrap.appendChild(searchRow);

  // Grid — built once; search mutates CSS classes only
  var grid = doc.createElement('div');
  grid.className = 'pf-brands-grid';
  wrap.appendChild(grid);
  renderBrands(grid, allBrands);

  // Search: grey out mismatches + float matches to front via CSS order
  searchInput.addEventListener('input', function () {
    var q = searchInput.value.trim().toLowerCase();
    applyBrandQuery(grid, countBadge, q, allBrands.length);
  });
}

// ---------------------------------------------------------------------------
// Init — called by the bundle dispatcher. No-op unless the container is present.
// ---------------------------------------------------------------------------
export function init() {
  var container = findContainer();
  if (!container) return; // not on a page with this widget

  var refs = buildScaffold(container);

  // Bearer key + base URL come from shared config via apiJson.
  apiJson('/api/filters/brands', null, null).then(function (data) {
    if (!data) {
      refs.skeleton.remove();
      var errEl = getTopDocument().createElement('div');
      errEl.className = 'pf-brands-empty';
      errEl.textContent = 'Could not load brands.';
      refs.wrap.appendChild(errEl);
      return;
    }
    var brands = Array.isArray(data.brands) ? data.brands : [];
    buildInteractiveUI(refs.wrap, refs.skeleton, brands);
  });
}

export var id = 'brands-dropdown';
